/**
 * Realtime Firestore subscription hooks. Each hook returns a small
 * { data, loading, error } record so pages get consistent loading/empty/error
 * handling. All reads rely on the admin custom claim (security rules grant
 * admins list/get across the relevant collections).
 */
import { useEffect, useState } from 'react';
import {
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  type Query,
  type DocumentReference,
} from 'firebase/firestore';

import {
  activityLogsCol,
  usersCol,
  productsCol,
  pushCampaignsCol,
  presenceCol,
  appConfigDoc,
  scratchCardsCol,
  scratchCardConfigDoc,
  type ActivityLogWithId,
} from '../lib/collections';
import type {
  User,
  Product,
  PushCampaign,
  AppConfig,
  ActivityEvent,
  Presence,
  ScratchCard,
  ScratchCardConfig,
} from '@mitang/shared';
import { errorMessage } from '../lib/errors';

export interface QueryState<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

/** Subscribe to a Firestore query and map snapshot docs to entities. */
function useCollection<T>(build: () => Query<T>, deps: unknown[]): QueryState<T[]> {
  const [state, setState] = useState<QueryState<T[]>>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    const q = build();
    const unsub = onSnapshot(
      q,
      (snap) => {
        setState({ data: snap.docs.map((d) => d.data()), loading: false, error: null });
      },
      (err) => {
        setState({ data: [], loading: false, error: errorMessage(err) });
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

/** Subscribe to a single document. */
function useDocument<T>(ref: DocumentReference<T>): QueryState<T | null> {
  const [state, setState] = useState<QueryState<T | null>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setState({ data: snap.exists() ? snap.data() : null, loading: false, error: null });
      },
      (err) => {
        setState({ data: null, loading: false, error: errorMessage(err) });
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}

/* --------------------------- Concrete hooks ------------------------------ */

/**
 * The dealer approval queue. On the free tier the separate
 * `registration_requests` collection is retired: a pending dealer is now just a
 * `users` doc with `status == 'pending'`. Newest first.
 */
export function usePendingUsers(): QueryState<User[]> {
  return useCollection<User>(
    () =>
      query(
        usersCol,
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
      ),
    [],
  );
}

/** All users (admin list). Sorting/filtering happens client-side. */
export function useUsers(): QueryState<User[]> {
  return useCollection<User>(() => query(usersCol, orderBy('createdAt', 'desc')), []);
}

/** Most-recent activity logs across all users (live feed). */
export function useRecentActivity(max = 40): QueryState<ActivityLogWithId[]> {
  return useCollection<ActivityLogWithId>(
    () => query(activityLogsCol, orderBy('ts', 'desc'), limit(max)),
    [max],
  );
}

/**
 * Activity logs since a cutoff timestamp (for dashboard KPIs / insights).
 * Ordered by ts desc; capped for safety.
 */
export function useActivitySince(sinceTs: number, max = 500): QueryState<ActivityLogWithId[]> {
  return useCollection<ActivityLogWithId>(
    () =>
      query(
        activityLogsCol,
        where('ts', '>=', sinceTs),
        orderBy('ts', 'desc'),
        limit(max),
      ),
    [sinceTs, max],
  );
}

/** A single user's activity, newest first (Activity Timeline drawer). */
export function useUserActivity(userId: string | null, max = 300): QueryState<ActivityLogWithId[]> {
  return useCollection<ActivityLogWithId>(
    () =>
      userId
        ? query(
            activityLogsCol,
            where('userId', '==', userId),
            orderBy('ts', 'desc'),
            limit(max),
          )
        : // Empty-user query: filter to an impossible id so we get [] quickly.
          query(activityLogsCol, where('userId', '==', '__none__'), limit(1)),
    [userId, max],
  );
}

/** Activity of a specific event type since a cutoff (e.g. app_open for charts). */
export function useEventActivitySince(
  event: ActivityEvent,
  sinceTs: number,
  max = 500,
): QueryState<ActivityLogWithId[]> {
  return useCollection<ActivityLogWithId>(
    () =>
      query(
        activityLogsCol,
        where('event', '==', event),
        where('ts', '>=', sinceTs),
        orderBy('ts', 'desc'),
        limit(max),
      ),
    [event, sinceTs, max],
  );
}

/** All active products (for per-category counts on the sync monitor). */
export function useActiveProducts(): QueryState<Product[]> {
  return useCollection<Product>(
    () => query(productsCol, where('active', '==', true)),
    [],
  );
}

/** Push campaign history, newest first. Sorts client-side by createdAt-ish. */
export function usePushCampaigns(max = 100): QueryState<PushCampaign[]> {
  return useCollection<PushCampaign>(() => query(pushCampaignsCol, limit(max)), [max]);
}

/**
 * Live presence docs, newest heartbeat first. `lastSeen desc` is a single-field
 * order, so no composite index is required. Online/offline classification is
 * time-based and re-evaluated by the Live page's ticker (see useNow).
 */
export function usePresence(): QueryState<Presence[]> {
  return useCollection<Presence>(
    () => query(presenceCol, orderBy('lastSeen', 'desc')),
    [],
  );
}

/** app_config/global — sync health + min version. */
export function useAppConfig(): QueryState<AppConfig | null> {
  return useDocument<AppConfig>(appConfigDoc);
}

/** app_config/scratchCard — scratch & win switch, round and prize table. */
export function useScratchCardConfig(): QueryState<ScratchCardConfig | null> {
  return useDocument<ScratchCardConfig>(scratchCardConfigDoc);
}

/**
 * Every scratch card ever drawn (all rounds), newest first. Single-field order,
 * so no composite index. Filtering by round/prize/status is client-side — the
 * whole set is one doc per dealer per round, which stays small.
 */
export function useScratchCards(): QueryState<ScratchCard[]> {
  return useCollection<ScratchCard>(() => query(scratchCardsCol, orderBy('drawnAt', 'desc')), []);
}
